import React from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  List,
  ListItemText,
  ListItem,
  Typography,
  Paper,
  Button,
} from "@mui/material";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, {
  AccordionSummaryProps,
  accordionSummaryClasses,
} from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import { styled } from "@mui/material/styles";
import LabelAddRemove from "../CustomLabels/LabelAddRemove";
import useCutsheetSelector from "./useCutsheetSelector";
import { inputLabelFormat, getOptions } from "./cutsheet.biz";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&::before": {
    display: "none",
  },
  marginBottom: 40,
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: "#7393B3",
  flexDirection: "row-reverse",
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    {
      transform: "rotate(90deg)",
    },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

export const StyledDivCol = styled("div")({
  display: "flex",
  flexDirection: "column",
});

const StyledDivColParent = styled("div")({
  display: "flex",
  flexDirection: "column",
  maxHeight: "100vh",
});

const StyledDivColChild = styled("div")({
  display: "flex",
  flexDirection: "column",
  overflowY: "auto", // Enables scrolling
});

const StyledDivColImg = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginLeft: 40,
  maxHeight: "100vh",
  [theme.breakpoints.down("md")]: {
    marginLeft: 0,
    paddingBottom: 40,
  },
}));

const StyledDivColImgChild = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
}));

const StyledDivRow = styled("div")({
  display: "flex",
  flexDirection: "row",
});

const StyledDivRowCenter = styled("div")({
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
});

const StyledDivRowResponsive = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  height: "100vh",
  overflow: "hidden",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column-reverse",
    height: "auto",
  },
}));

const StyledGridDiv = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "400px 400px",
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "400px",
  },
}));

const StyledGridItm = styled("div")({
  height: 70,
});

const StyledInput = styled("input")({
  width: 120,
});

const StyledTypography = styled(Typography)({
  paddingRight: 10,
  fontWeight: "bold",
});

const getListStyle = () => ({
  overflow: "auto",
  height: "auto",
  flex: "1 0 auto",
  width: 500,
});

const getItemStyle = (isDragging: boolean, dragStyle: any) => ({
  userSelect: "none",

  // change background colour if dragging
  position: "relative",
  backgroundColor: isDragging ? "lightgrey" : "#7393B3",
  borderBottom: "2px solid black",

  // styles we need to apply on draggables
  ...dragStyle,
});

const CutsheetEdits = () => {
  const {
    typeOfDataArr,
    hiddenFilters,
    uploadNewCutsheetImage,
    handleChange,
    onDragEnd,
    submitChanges,
    removeEntry,
    addEntry,
    handleFilterChange,
    handleTypeOf,
    handleDoorType,
    data,
    imageData,
    typeOfData,
    doorType,
    fetching,
    filter,
  } = useCutsheetSelector();

  return (
    <StyledDivCol style={{ alignItems: "center" }}>
      <Accordion>
        <AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
          <Typography component="span">Cutsheet Filters</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <StyledGridDiv>
            <StyledGridItm style={{ padding: 10 }}>
              <StyledDivRowCenter>
                <Typography style={{ paddingRight: 10 }}>Door Type:</Typography>
                <select onChange={handleDoorType} value={doorType}>
                  {!doorType && <option value=""></option>}
                  {["Bi-fold", "Slider", "Swing", "Pivot", "Fixed Panel"].map(
                    (door) => (
                      <option key={door} value={door}>
                        {door}
                      </option>
                    ),
                  )}
                </select>
              </StyledDivRowCenter>
            </StyledGridItm>

            <StyledGridItm style={{ padding: 10 }}>
              <StyledDivRowCenter>
                <Typography style={{ paddingRight: 10 }}>
                  Frame Piece:
                </Typography>
                <select onChange={handleTypeOf} value={typeOfData}>
                  {!typeOfData && <option value=""></option>}
                  {typeOfDataArr.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </StyledDivRowCenter>
            </StyledGridItm>

            {doorType && typeOfData && Object.keys(filter)?.length
              ? Object.keys(filter)
                  .filter((key) => !hiddenFilters.includes(key))
                  .map((key) =>
                      <StyledGridItm key={key} style={{ padding: 10 }}>
                        <StyledDivRowCenter>
                          <Typography style={{ paddingRight: 10 }}>
                            {inputLabelFormat(key)}:
                          </Typography>
                          <select
                            onChange={(e) => handleFilterChange(e, key)}
                            value={filter[key]}
                            key={key}
                          >
                            {getOptions(doorType, key).map((opt: string) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        </StyledDivRowCenter>
                      </StyledGridItm>
                  )
              : null}
          </StyledGridDiv>
        </AccordionDetails>
      </Accordion>

      {doorType === "Slider" && (
        <StyledDivRowCenter style={{ paddingBottom: 40 }}>
          <Typography variant="subtitle2">
            * Any update to a Slider part’s height, width, or species will
            automatically update all other Slider parts that share the same part
            letter
          </Typography>
        </StyledDivRowCenter>
      )}

      {data.length ? (
        <StyledDivRowResponsive style={{ position: "relative" }}>
          <StyledDivColParent>
            <StyledDivColChild>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="quoteCart">
                  {(provided, snapshot) => (
                    <Paper
                      elevation={2}
                      style={{ backgroundColor: "#7393B3", height: "auto" }}
                    >
                      <List
                        style={getListStyle()}
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        {data.map((text, index) => (
                          <Draggable
                            key={text._id}
                            index={index}
                            draggableId={text._id + "_dragID"}
                          >
                            {(provided, snapshot) => (
                              <ListItem
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={getItemStyle(
                                  snapshot.isDragging,
                                  provided.draggableProps.style,
                                )}
                              >
                                <ListItemText
                                  primary={
                                    <StyledDivCol>
                                      <StyledDivRow
                                        style={{
                                          justifyContent: "space-between",
                                        }}
                                      >
                                        <StyledDivRow style={{ padding: 5 }}>
                                          <StyledTypography>
                                            Part:
                                          </StyledTypography>
                                          <StyledInput
                                            onChange={(event) =>
                                              handleChange(
                                                event,
                                                text._id,
                                                "part",
                                              )
                                            }
                                            value={text.part}
                                          />
                                        </StyledDivRow>

                                        <StyledDivRow style={{ padding: 5 }}>
                                          <StyledTypography>
                                            Species:
                                          </StyledTypography>

                                          <StyledInput
                                            onChange={(event) =>
                                              handleChange(
                                                event,
                                                text._id,
                                                "species",
                                              )
                                            }
                                            value={text.species}
                                          />
                                        </StyledDivRow>
                                      </StyledDivRow>
                                      <StyledDivRow
                                        style={{
                                          justifyContent: "space-between",
                                        }}
                                      >
                                        <StyledDivRow style={{ padding: 5 }}>
                                          <StyledTypography>
                                            Width:
                                          </StyledTypography>

                                          <StyledInput
                                            onChange={(event) =>
                                              handleChange(
                                                event,
                                                text._id,
                                                "width",
                                              )
                                            }
                                            value={text.width}
                                          />
                                        </StyledDivRow>
                                        <StyledDivRow style={{ padding: 5 }}>
                                          <StyledTypography>
                                            Quantity:
                                          </StyledTypography>
                                          <StyledInput
                                            onChange={(event) =>
                                              handleChange(
                                                event,
                                                text._id,
                                                "qty",
                                              )
                                            }
                                            value={text.qty}
                                          />
                                        </StyledDivRow>
                                      </StyledDivRow>
                                      <StyledDivRow>
                                        <StyledDivRow style={{ padding: 5 }}>
                                          <StyledTypography>
                                            Height:
                                          </StyledTypography>
                                          <StyledInput
                                            onChange={(event) =>
                                              handleChange(
                                                event,
                                                text._id,
                                                "height",
                                              )
                                            }
                                            value={text.height}
                                          />
                                        </StyledDivRow>
                                      </StyledDivRow>
                                    </StyledDivCol>
                                  }
                                />
                              </ListItem>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        <LabelAddRemove
                          isEnd={true}
                          index={data.length - 1}
                          addNewNote={addEntry}
                          removeNote={removeEntry}
                        />
                      </List>
                    </Paper>
                  )}
                </Droppable>
              </DragDropContext>
            </StyledDivColChild>

            <StyledDivRow style={{ margin: 40, alignSelf: "center" }}>
              <Button
                disabled={fetching}
                onClick={submitChanges}
                variant="contained"
              >
                Submit
              </Button>
            </StyledDivRow>
          </StyledDivColParent>

          <StyledDivColImg>
            <StyledDivColImgChild>
              <Paper elevation={2}>
                <img
                  id="cs-img"
                  alt="Cutsheet Img not found"
                  style={{
                    width: 450,
                    height: "auto",
                  }}
                />
              </Paper>
            </StyledDivColImgChild>

            <Typography
              style={{ paddingTop: 20, alignSelf: "center" }}
              variant="subtitle2"
            >
              Uploaded Image - width: {imageData.metadata.width}; height:{" "}
              {imageData.metadata.height}; type: {imageData.metadata.type}
            </Typography>

            <StyledDivRow
              style={{ paddingTop: 40, alignSelf: "center", marginLeft: 20 }}
            >
              <Typography style={{ fontWeight: "bold" }}>Upload:</Typography>
              <input
                style={{ marginLeft: 20 }}
                disabled={fetching}
                type="file"
                onChange={uploadNewCutsheetImage}
              />
            </StyledDivRow>

            {!fetching && imageData.uploadResult === "error" ? (
              <StyledDivRow style={{ alignSelf: "center", paddingTop: 20 }}>
                <Typography style={{ color: "red" }}>
                  Could not upload
                </Typography>
                <ErrorIcon style={{ color: "red" }} />
              </StyledDivRow>
            ) : !fetching && imageData.uploadResult === "success" ? (
              <StyledDivRow style={{ alignSelf: "center", paddingTop: 20 }}>
                <Typography style={{ color: "green" }}>
                  Upload successful
                </Typography>
                <CheckCircleIcon color="success" />
              </StyledDivRow>
            ) : null}
          </StyledDivColImg>
        </StyledDivRowResponsive>
      ) : (
        <StyledDivRow>
          <Typography>No data found</Typography>
        </StyledDivRow>
      )}
    </StyledDivCol>
  );
};

export default CutsheetEdits;
